import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Building2, User, Shield, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PartnerInviteAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, login } = useContext(AuthContext);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  
  // New user form
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const validateToken = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/partner-invites/token/${token}`);
      setInvite(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invite link');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  const handleAccept = async (e) => {
    e.preventDefault();
    
    // Validation for new users
    if (!invite.user_exists) {
      if (!name.trim()) {
        toast.error('Please enter your name');
        return;
      }
      if (password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      if (password !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
    }
    
    setSubmitting(true);
    try {
      const config = {};
      
      // If existing user, include auth token
      if (invite.user_exists && user) {
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          config.headers = { Authorization: `Bearer ${storedToken}` };
        }
      }
      
      const payload = invite.user_exists ? {} : { name, password };
      
      const response = await axios.post(
        `${API}/partner-invites/token/${token}/accept`,
        payload,
        config
      );
      
      // Login with the returned token
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
        login(response.data.user);
      }
      
      toast.success('Welcome! You have joined the organization.');
      navigate('/partner/dashboard');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to accept invite';
      toast.error(errorMsg);
      
      // If login required, redirect to login with return URL
      if (err.response?.status === 401) {
        localStorage.setItem('inviteReturnUrl', `/partner/invite/${token}`);
        navigate('/login');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleDisplay = (role) => {
    return role === 'partner_admin' ? 'Partner Administrator' : 'Partner Staff';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF4EA]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#8B2E3C] animate-spin mx-auto mb-4" />
          <p className="text-[#666666]">Validating invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF4EA] px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-[#222222] mb-2">Invalid Invite</h1>
          <p className="text-[#666666] mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBF4EA] px-4 py-12">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-[#8B2E3C] rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#222222]">You're Invited!</h1>
          <p className="text-[#666666] mt-2">Join {invite.org_name} on Community Kindness Circle</p>
        </div>

        {/* Invite Details Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6" data-testid="invite-details">
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-[#FBF4EA] rounded-lg">
              <Building2 className="w-5 h-5 text-[#8B2E3C]" />
              <div>
                <div className="text-sm text-[#666666]">Organization</div>
                <div className="font-medium text-[#222222]">{invite.org_name}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-[#FBF4EA] rounded-lg">
              <Shield className="w-5 h-5 text-[#C9A227]" />
              <div>
                <div className="text-sm text-[#666666]">Your Role</div>
                <div className="font-medium text-[#222222]">{getRoleDisplay(invite.role)}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-[#FBF4EA] rounded-lg">
              <User className="w-5 h-5 text-[#8B2E3C]" />
              <div>
                <div className="text-sm text-[#666666]">Email</div>
                <div className="font-medium text-[#222222]">{invite.email}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Accept Form */}
        <div className="bg-white rounded-xl shadow-lg p-6" data-testid="accept-form">
          {invite.user_exists ? (
            // Existing user - just accept
            <div>
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg mb-4">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-green-700">Account found: <strong>{invite.user_name}</strong></span>
              </div>
              
              {user && user.email.toLowerCase() === invite.email.toLowerCase() ? (
                <button
                  onClick={handleAccept}
                  disabled={submitting}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="accept-invite-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Accept & Join Organization
                    </>
                  )}
                </button>
              ) : (
                <div>
                  <p className="text-sm text-[#666666] mb-4">
                    Please log in with <strong>{invite.email}</strong> to accept this invite.
                  </p>
                  <button
                    onClick={() => {
                      localStorage.setItem('inviteReturnUrl', `/partner/invite/${token}`);
                      navigate('/login');
                    }}
                    className="btn-primary w-full"
                  >
                    Log In to Accept
                  </button>
                </div>
              )}
            </div>
          ) : (
            // New user - create account
            <form onSubmit={handleAccept}>
              <h3 className="font-bold text-[#222222] mb-4">Create Your Account</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input
                    type="email"
                    value={invite.email}
                    disabled
                    className="input-field bg-gray-100 cursor-not-allowed"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Your Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your full name"
                    className="input-field"
                    required
                    data-testid="invite-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password (min 6 characters)"
                    className="input-field"
                    required
                    minLength={6}
                    data-testid="invite-password-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="input-field"
                    required
                    data-testid="invite-confirm-password-input"
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
                data-testid="create-account-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Create Account & Join
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-[#666666] mt-6">
          By accepting, you agree to the Community Kindness Circle terms of service.
        </p>
      </div>
    </div>
  );
};

export default PartnerInviteAccept;
